import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import axios from "src/axios";
import useSession from "src/Hooks/useSession";
import { useSnackbar } from "notistack";
import qs from "qs";
import { useMemo } from "react";
import { applyQrTheme, getSortedMenuCategoriesAndItems } from "src/utils";

export function useAllRestaurantsAction(params) {
  const { enqueueSnackbar } = useSnackbar();
  const fetch_near_by_location = async () =>
    await axios.get("/web/geolocation/fetch-near-by-locations", {
      params: params,
    });

  let enabled = params && params.longitude && params.latitude ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["restaurants_list", params],
    enabled: enabled,
    queryFn: fetch_near_by_location,
    onError: (e) => {
      enqueueSnackbar("Something went wrong, Failed to fetch restaurants", {
        variant: "error",
      });
    },
  });

  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useFetchPropertyByPropertyIdAction(params) {
  const fetch_property_by_property_id = async () => {
    const response = await axios.get("/web/property/fetch-property", {
      params: params,
    });
    // Single place the property enters the storefront, so this is where the QR-only appearance
    // overrides get folded in — see applyQrTheme.
    if (!response?.data?.data) return response;
    return {
      ...response,
      data: { ...response.data, data: applyQrTheme(response.data.data) },
    };
  };
  let enabled = params && params.enabled && params.property_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["property_by_property_id", params],
    enabled: enabled,
    queryFn: fetch_property_by_property_id,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useFetchVendorsAction(params) {
  const fetch_vendors = async () =>
    await axios.get("/web/property/fetch-vendors", {
      params: params,
    });
  let enabled = params && params.enabled && params.property_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["vendors_by_property_id", params],
    enabled: enabled,
    queryFn: fetch_vendors,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useFetchPlanStatusesAction(params) {
  const fetch_plan_statuses = async () =>
    await axios.get("/web/fetch-plan-statuses", {
      params: params,
    });
  let enabled = params && params.property_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["plan_statuses", params],
    enabled: enabled,
    queryFn: fetch_plan_statuses,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useAllMenuAction(params) {
  const fetch_all_menu = async () =>
    await axios.get("/web/fetch-all-menu", {
      params: params,
    });
  let enabled = params && params.property_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["menu_list", params],
    enabled: enabled,
    queryFn: fetch_all_menu,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useAllMenuAndItemsAction(params) {
  const fetch_all_menu_and_items = async () =>
    await axios.get("/web/fetch-all-menu-categories-and-items", {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    });
  let enabled = params && params.property_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["menu_and_items_list", params],
    enabled: enabled,
    queryFn: fetch_all_menu_and_items,
  });

  const sortedMenuAndItems = useMemo(() => {
    if (data?.data?.data && !params?.is_unified_menu) {
      return getSortedMenuCategoriesAndItems(data?.data?.data);
    }
    return data?.data?.data;
  }, [data, params?.is_unified_menu]);

  return {
    data: sortedMenuAndItems,
    isLoading: enabled && isLoading,
    error,
    isError,
    refetch,
  };
}

export function useAllCategoriesAndItemsAction(params) {
  const fetch_all_categories_and_items = async () =>
    await axios.get("/web/fetch-all-categories-and-items", {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    });
  let enabled = params && params.menu_id ? true : false;
  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["categories_and_items_list", params],
    enabled: enabled,
    queryFn: fetch_all_categories_and_items,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useAllFiltersAction(params) {
  const fetch_all_filters_items = async () =>
    await axios.get("web/web-service/fetch-all-filters", {
      params: params,
    });
  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["filters_list"],
    queryFn: fetch_all_filters_items,
  });
  return { data, isLoading, error, isError, refetch };
}

export function useFetchItemByItemIdAction(params) {
  const fetch_item_by_item_id = async () =>
    await axios.get("/web/fetch-item-by-item-id", {
      params: params,
    });
  let enabled = params && params.item_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["item_by_item_id", params],
    enabled: enabled,
    queryFn: fetch_item_by_item_id,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useFetchComboGroupsAndItemsAction(params) {
  const fetch_combo_groups_and_items = async () =>
    await axios.get("/web/fetch-combo-groups-and-items", {
      params: params,
    });
  let enabled = params && params.item_id && params?.is_combo ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["combo_groups_and_items", params],
    enabled: enabled,
    queryFn: fetch_combo_groups_and_items,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useAllAddonsAction({ is_addon_available = true, ...params }) {
  const fetch_all_addons = async () =>
    await axios.get("/web/fetch-all-addons", {
      params: params,
    });
  let enabled =
    params && params.item_id && !params?.is_combo && is_addon_available
      ? true
      : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["addons_list", params],
    enabled: enabled,
    queryFn: fetch_all_addons,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useCartDetailsAction(params) {
  const { dispatch } = useSession();
  const fetch_cart = async () =>
    await axios.get("/web/cart/fetch-cart-by-cart-id", {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    });
  let enabled = params && params.cart_id?.length ? true : false;
  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["cart_details", params],
    enabled: enabled,
    queryFn: fetch_cart,
    // refetchOnMount: "always",
    onSuccess: (data, variables, context) => {
      if (!data?.data?.status) {
        dispatch({
          type: "REMOVE_CART_ID",
        });
        dispatch({
          type: "REMOVE_NOTES",
        });
      }
    },
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}

export function useOrderDetailsAction(params) {
  const fetch_order = async () =>
    await axios.get("/web/order/fetch-order-details-by-order-id", {
      params: params,
    });
  let enabled = params && params.order_id ? true : false;
  const { isLoading, isError, data, error, refetch, isFetching } = useQuery({
    queryKey: ["order_details", params],
    enabled: enabled,
    queryFn: fetch_order,
    networkMode: "always",
    cacheTime: 0,
    staleTime: 0,
  });
  return {
    data,
    isLoading: enabled && isLoading,
    error,
    isError,
    refetch,
    isFetching,
  };
}

export function useAddItemsToCartAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const { dispatch } = useSession();
  const mutation = useMutation(
    async (data) => {
      return await axios.post("/web/cart/add-cart-items", data);
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          dispatch({
            type: "SET_CART_ID",
            payload: {
              cart_id: data?.data?.data?.cart_id,
            },
          });
          enqueueSnackbar("Added item to cart succesfully", {
            variant: "success",
          });
          successCallback && successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useUpdateCartItemAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post("/web/cart/update-cart-addon-quantity", data);
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Updated cart item succesfully", {
            variant: "success",
          });
          successCallback && successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
      onError: (e) => {
        enqueueSnackbar(e || "Something went wrong", { variant: "error" });
      },
    }
  );

  return { mutation: mutation };
}

export function useUpdateCartItemAndAddonQuantityAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(
        "/web/cart/update-cart-item-and-addon-quantity",
        data
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Updated cart item succesfully", {
            variant: "success",
          });
          successCallback && successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function usePlaceOrderAction() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { dispatch } = useSession();
  const mutation = useMutation(
    async (data) => {
      return await axios.post("/web/order/place-order", data);
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          navigate(`/payment/${data?.data?.data?.order_id}`);
          dispatch({
            type: "REMOVE_CART_ID",
          });
          dispatch({
            type: "REMOVE_NOTES",
          });
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function usePayLaterAction(successCallback) {
  const mutation = useMutation(
    async (data) => {
      return await axios.post("/web/order/pay-later", null, { params: data });
    },
    {
      onSuccess: (data, variables, context) => {
        successCallback && successCallback();
      },
    }
  );

  return { mutation: mutation };
}

export function getPaymentMethods(params) {
  return axios.get("/web/adyen/payment-methods", {
    params: params,
  });
}

export function getPaymentDetails(params) {
  return axios.get("/web/adyen/payment-details", {
    params: params,
  });
}

export function getPropertyTimingDetails(params) {
  return axios.get("/web/property/fetch-property", {
    params: params,
  });
}

export function useInitiatePaymentAction() {
  const mutation = useMutation(
    async (data) => {
      const pspCode = data?.psp_provider;

      return await axios.post("/web/adyen/initiate-payment", data?.params, {
        headers: {
          "psp-code": pspCode,
          "currency-code": data?.params?.currency_code || "GBP",
          "payment-type": "ECOM",
        },
      });
    },
    {
      onSuccess: (data, variables, context) => {
        variables?.successCallback &&
          variables?.successCallback(data, variables, context);
      },
      onError: (error, variables, context) => {
        console.error("API call failed:", error);
        variables?.errorCallback &&
          variables?.errorCallback(error, variables, context);
      },
    }
  );

  return { mutation: mutation };
}
export function useInitiatePaymentV2Action() {
  const mutation = useMutation(
    async (data) => {
      const pspCode = data?.psp_provider;

      return await axios.post("/web/adyen/v2/initiate-payment", data?.params, {
        headers: {
          "psp-code": pspCode,
          "currency-code": data?.params?.currency_code || "GBP",
          "payment-type": "ECOM",
        },
      });
    },
    {
      onSuccess: (data, variables, context) => {
        variables?.successCallback &&
          variables?.successCallback(data, variables, context);
      },
      onError: (error, variables, context) => {
        console.error("API call failed:", error);
        variables?.errorCallback &&
          variables?.errorCallback(error, variables, context);
      },
    }
  );

  return { mutation: mutation };
}
export function useCancelPaymentAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(
        `/web/adyen/cancel-payment?order_id=${data?.order_id}`
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (!data?.data?.status) {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useApplyDiscountAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(
        `/web/order/apply-discount?order_id=${
          data?.order_id
        }&discount_code=${encodeURIComponent(data?.discount_code)}`
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Discount Applied", {
            variant: "success",
          });
          successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useRemoveDiscountAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(
        `/web/order/cancel-discount?order_id=${data?.order_id}`
      );
    },
    {
      onSuccess: (data) => {
        if (data?.data?.status) {
          enqueueSnackbar("Discount Removed", {
            variant: "success",
          });
          successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useAmmendOrderAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(`/web/order/amend-order`, null, { params: data });
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          successCallback && successCallback(data?.data);
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
          navigate("/menu");
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useRatingsAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(`/web/order/ratings`, null, {
        params: data,
      });
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Feedback Submitted Succesfully", {
            variant: "success",
          });
          successCallback && successCallback(data?.data);
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useAddTipAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.put(
        `/web/order/add-tip?order_id=${data?.order_id}&tip_amount=${data?.tip_amount}`
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Tip added", {
            variant: "success",
          });
          successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}

export function useDeleteTipAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.delete(
        `/web/order/delete-tip?order_id=${data?.order_id}`
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Tip removed", {
            variant: "success",
          });
          successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation: mutation };
}
// Is this point inside the venue's delivery area? Same endpoint the website-ordering plugin uses.
export function checkDeliveryInRange(params) {
  return axios.get("/web/geolocation/is-delivery-in-range", { params });
}

export function useDeliveryTimings(params) {
  const fetch_delivery_timings = async () =>
    await axios.get("/web/property/fetch-timings", {
      params: params,
    });
  let enabled =
    params && params.property_id && params.slot_time_in_mins ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["delivery_timings", params],
    enabled: enabled,
    queryFn: fetch_delivery_timings,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}
export function useAddServiceChargeAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.post(
        `/web/order/add-service-charge?order_id=${data?.order_id}`
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Service charge added", {
            variant: "success",
          });
          successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation };
}

export function useDeleteServiceChargeAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    async (data) => {
      return await axios.put(
        `/web/order/delete-service-charge?order_id=${data?.order_id}`
      );
    },
    {
      onSuccess: (data, variables, context) => {
        if (data?.data?.status) {
          enqueueSnackbar("Service charge removed", {
            variant: "success",
          });
          successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
    }
  );

  return { mutation };
}
export function useFetchMealDealSuggestions(params) {
  const fetchSuggestions = async () => {
    const { data } = await axios.get("/web/cart/fetch-suggestions", {
      params,
    });
    return data;
  };

  const enabled =
    params?.property_id && params?.item_id && params?.order_type ? true : false;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["meal_deal_suggestions", params],
    enabled,
    queryFn: fetchSuggestions,
  });

  return { data, isLoading: enabled && isLoading, isError, error, refetch };
}
export function useAddMealDealCartAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();
  const { dispatch } = useSession();

  const mutation = useMutation(
    async ({ mealDealId, payload }) => {
      return await axios.post(
        `/web/cart/add-meal-cart-items?meal_deal_id=${mealDealId}`,
        payload
      );
    },
    {
      onSuccess: (data) => {
        if (data?.data?.status) {
          dispatch({
            type: "SET_CART_ID",
            payload: {
              cart_id: data?.data?.data?.cart_id,
            },
          });
          enqueueSnackbar("Meal deal added to cart successfully", {
            variant: "success",
          });
          successCallback && successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
      onError: (error) => {
        enqueueSnackbar(error?.message || "Failed to add meal deal", {
          variant: "error",
        });
      },
    }
  );

  return { mutation };
}

export function useSelectFreebieItemAction(successCallback) {
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation(
    async ({ cart_id, offer_id, item_id }) => {
      return await axios.post("/web/cart/select-freebie-item", {
        cart_id,
        offer_id,
        item_id,
      });
    },
    {
      onSuccess: (data) => {
        if (data?.data?.status) {
          successCallback && successCallback();
        } else {
          enqueueSnackbar(data?.data?.message || "Something went wrong", {
            variant: "error",
          });
        }
      },
      onError: (error) => {
        enqueueSnackbar(error?.message || "Failed to update free item", {
          variant: "error",
        });
      },
    }
  );

  return { mutation };
}

// Every ACTIVE freebie offer for a property, eligible/locked against
// cart_total (0 for a menu-page call before a cart exists). Feeds both the
// menu carousel and the cart's "see all freebies" modal.
export function useFreebieOffersAction(params) {
  const fetch_freebie_offers = async () =>
    await axios.get("/web/cart/freebie-offers", {
      params: params,
    });
  let enabled = params && params.property_id ? true : false;

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["freebie_offers", params],
    enabled: enabled,
    queryFn: fetch_freebie_offers,
  });
  return { data, isLoading: enabled && isLoading, error, isError, refetch };
}
